import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CalendarIcon,
  CheckCircle2,
  ChevronDown,
  Copy,
  Loader2,
  Save,
  ShieldCheck
} from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { pricingApi, quoteApi } from "@/lib/adminApi";
import {
  applyRecommendationToDraft,
  CONSULTANT_PROMPTS,
  getCommercialTemplate,
  getServiceProfile,
  COMMERCIAL_TEMPLATES,
  RECOMMENDABLE_FIELD_LABELS
} from "@/lib/serviceProfiles";
import {
  CLIENT_STATUS_LABEL_BY_INTERNAL,
  DELIVERY_METHODS,
  DEFAULT_DELIVERY_CODE,
  TEMPLATE_TO_DELIVERY_CODE
} from "@/lib/sbcStandard";
import { ClientDocumentPreview } from "./ClientDocumentPreview";
import { RecommendationPreviewDialog } from "./RecommendationPreview";

// Phase 1C — commercial deployment governance blocks. Static, professional,
// cautious wording. These are appended to the client-facing quote preview
// (both on-screen and clipboard). They are NOT stored on the quote record and
// they never mention legal / audit / registration / compliance guarantees or
// tax advice.
const OPERATOR_LEGAL_LINE =
  "Spiral Beyond Compliance is operated by JOHN MARTIN KENNEALLY ABN 82 097 590 964.";
const QUOTE_ACCEPTANCE_INSTRUCTION =
  "To accept this quote, reply in writing (email or signed acceptance) quoting the quote number above. Work commences after acceptance and any required deposit are received.";
const PAYMENT_TERMS_LINE =
  "Payment terms: invoicing is administered manually by the consultancy. Payment method, reference and reconciliation details are provided on the manually issued invoice. GST status is confirmed on the invoice.";
const DEPOSIT_GOVERNANCE_CAVEAT =
  "Commercial note: deposit, balance and payment terms are commercial administration terms only. They do not guarantee audit, registration or compliance outcomes and do not constitute legal, accounting or taxation advice.";
const NON_PAYMENT_NOTE =
  "If agreed payment terms lapse, further work may be paused until arrears are cleared. Scope changes require a fresh written quote before additional work is undertaken.";
const VALIDITY_FALLBACK =
  "Quote validity: if a validity date is not shown above, request a fresh quote before acceptance.";

// -------- Enum vocabularies (values unchanged; labels are display-only) --------

const QUOTE_STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "REQUIRES_CLARIFICATION", label: "Requires clarification" },
  { value: "READY_FOR_OWNER_REVIEW", label: "Ready for owner review" },
  { value: "APPROVED", label: "Approved" },
  { value: "ISSUED", label: "Issued" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "DECLINED", label: "Declined" },
  { value: "EXPIRED", label: "Expired" },
  { value: "SUPERSEDED", label: "Superseded" }
];

const GST_SETTING_OPTIONS = [
  { value: "UNCONFIRMED", label: "GST — not yet confirmed" },
  { value: "INCLUDED", label: "GST included in fee" },
  { value: "EXCLUDED", label: "GST added on top of fee" },
  { value: "NOT_APPLICABLE", label: "GST not applicable" }
];

const PRICING_BASIS_OPTIONS = [
  { value: "FIXED_RANGE", label: "Fixed price range" },
  { value: "HOURLY", label: "Hourly rate" }
];

const URGENCY_CANONICAL = [
  { value: "Standard", label: "Standard turnaround" },
  { value: "Priority", label: "Priority — faster than standard" },
  { value: "Urgent", label: "Urgent — must-start-soon" },
  { value: "Must start immediately", label: "Must start immediately" }
];

const PROVIDER_SIZE_CANONICAL = [
  { value: "Sole trader", label: "Sole trader / 1 person" },
  { value: "Small (1–5 staff)", label: "Small — 1 to 5 staff" },
  { value: "Medium (6–20 staff)", label: "Medium — 6 to 20 staff" },
  { value: "Large (21+ staff)", label: "Large — 21+ staff" },
  { value: "Multi-site provider", label: "Multi-site provider" }
];

// -------- Draft shape (payload keys UNCHANGED) --------

const emptyDraft = {
  quoteNumber: "",
  dateCreated: "",
  expiryDate: "",
  providerLegalEntity: "",
  tradingName: "",
  abn: "",
  contactPerson: "",
  contactEmail: "",
  contactPhone: "",
  selectedServiceCode: "",
  providerSizeOrCohort: "",
  urgency: "",
  assessmentScope: "",
  documentReviewScope: "",
  estimatedHours: "",
  pricingBasis: "FIXED_RANGE",
  pricingRangeMin: "",
  pricingRangeMax: "",
  proposedFinalPrice: "",
  gstSetting: "UNCONFIRMED",
  depositAmount: "",
  balanceTerms: "",
  inclusions: "",
  exclusions: "",
  evidenceRequiredFromProvider: "",
  deliverables: "",
  estimatedTimeframe: "",
  additionalProfessionalReferrals: "",
  internalCommercialNotes: "",
  clientFacingNotes: ""
};

// -------- Section registry (drives collapsibles + error routing) --------

const SECTION_FIELDS = {
  identifiers: ["quoteNumber", "dateCreated", "expiryDate"],
  provider: [
    "providerLegalEntity",
    "tradingName",
    "abn",
    "contactPerson",
    "contactEmail",
    "contactPhone",
    "providerSizeOrCohort",
    "urgency"
  ],
  service: ["selectedServiceCode", "assessmentScope", "documentReviewScope"],
  pricing: [
    "estimatedHours",
    "pricingBasis",
    "pricingRangeMin",
    "pricingRangeMax",
    "proposedFinalPrice",
    "gstSetting",
    "depositAmount",
    "balanceTerms"
  ],
  deliverables: [
    "inclusions",
    "exclusions",
    "evidenceRequiredFromProvider",
    "deliverables",
    "estimatedTimeframe",
    "additionalProfessionalReferrals"
  ],
  notes: ["internalCommercialNotes", "clientFacingNotes"]
};

const NUMERIC_KEYS = new Set([
  "estimatedHours",
  "pricingRangeMin",
  "pricingRangeMax",
  "proposedFinalPrice",
  "depositAmount"
]);

const CURRENCY_KEYS = new Set([
  "pricingRangeMin",
  "pricingRangeMax",
  "proposedFinalPrice",
  "depositAmount"
]);

// -------- Helpers --------

function toPayload(draft) {
  const p = {};
  Object.entries(draft).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;
    if (NUMERIC_KEYS.has(k)) {
      const n = Number(v);
      if (!Number.isNaN(n)) p[k] = n;
    } else {
      p[k] = v;
    }
  });
  return p;
}

// Presentation-only Australian-style currency formatter. Accepts any raw
// numeric string in state (e.g. "1500", "1500.5", "1500.00") and returns
// "1,500", "1,500.5", "1,500.00". Empty / non-numeric input returns "".
// Never returns a $-prefixed string (the $ is a visual adornment on the
// input itself).
function formatAudNumber(raw) {
  if (raw === null || raw === undefined) return "";
  const s = String(raw).trim();
  if (s === "") return "";
  // Reject non-numeric input rather than crash — caller shows raw string.
  if (!/^-?\d+(?:\.\d+)?$/.test(s)) return s;
  const [intPart, decPart] = s.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
}

// Sanitises free typing into a raw numeric string. Keeps digits and at most
// one decimal point. Preserves trailing "." while user is mid-type so that
// e.g. "1500." remains valid intermediate state. Empty allowed.
function sanitiseCurrencyInput(text) {
  if (text === null || text === undefined) return "";
  let s = String(text).replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  // Strip leading zeros unless value is exactly "0" or begins with "0."
  if (/^0\d/.test(s)) s = s.replace(/^0+/, "");
  return s;
}

// Parse an ISO YYYY-MM-DD string. Returns a Date or null. Any non-ISO string
// (including legacy freeform values) is treated as null so the picker won't
// crash — the raw stored value is preserved and displayed as-is.
function parseIsoDate(value) {
  if (!value) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
}

// Format a stored value as DD/MM/YYYY for display. If the stored value is
// not ISO YYYY-MM-DD (legacy / unknown format), return it verbatim so no
// data is silently lost.
function formatDateDisplay(value) {
  if (!value) return "";
  const d = parseIsoDate(value);
  if (d) return format(d, "dd/MM/yyyy");
  return String(value);
}

function toIsoDate(date) {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
}

// Human-readable field labels for validation-error routing.
const FIELD_LABELS = {
  quoteNumber: "Quote number",
  dateCreated: "Date created",
  expiryDate: "Expiry date",
  providerLegalEntity: "Provider legal entity",
  tradingName: "Trading name",
  abn: "ABN",
  contactPerson: "Contact person",
  contactEmail: "Contact email",
  contactPhone: "Contact phone",
  selectedServiceCode: "Selected service",
  providerSizeOrCohort: "Provider size / cohort",
  urgency: "Urgency",
  assessmentScope: "Assessment scope",
  documentReviewScope: "Document review scope",
  estimatedHours: "Estimated hours",
  pricingBasis: "Pricing basis",
  pricingRangeMin: "Fee range minimum",
  pricingRangeMax: "Fee range maximum",
  proposedFinalPrice: "Proposed final fee",
  gstSetting: "GST setting",
  depositAmount: "Deposit amount",
  balanceTerms: "Balance terms",
  inclusions: "Inclusions",
  exclusions: "Exclusions",
  evidenceRequiredFromProvider: "Evidence required from provider",
  deliverables: "Deliverables",
  estimatedTimeframe: "Estimated timeframe",
  additionalProfessionalReferrals: "Additional professional referrals",
  internalCommercialNotes: "Internal commercial notes",
  clientFacingNotes: "Client-facing notes"
};

function sectionForField(fieldKey) {
  for (const [section, fields] of Object.entries(SECTION_FIELDS)) {
    if (fields.includes(fieldKey)) return section;
  }
  return null;
}

// -------- Main component --------

export function QuoteBuilder({ quote, onSaved }) {
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [priceItems, setPriceItems] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pendingServiceCode, setPendingServiceCode] = useState(null);
  const [errorsByField, setErrorsByField] = useState({});
  const [urgencyCustomMode, setUrgencyCustomMode] = useState(false);
  const [sizeCustomMode, setSizeCustomMode] = useState(false);
  // QB-02B — provenance of consultant-authored fields. Any field the
  // consultant has edited manually is added here so the recommendation
  // engine will never silently overwrite it. Fields populated by a
  // recommendation are NOT added — so switching services refreshes
  // generated content without touching consultant work.
  const [touchedFields, setTouchedFields] = useState(new Set());
  // QB-02B — pending commercial-content regeneration decision (open state
  // for the second-tier dialog that follows the pricing dialog).
  const [pendingRegen, setPendingRegen] = useState(null);
  // QB-02B — consultant prompts (session-only, non-persisted decision
  // support). A map of prompt id → checked boolean.
  const [promptChecks, setPromptChecks] = useState({});
  // QB-02C — SBC-001 §1.9 Delivery Method (session-only presentation
  // control; not persisted to the backend — see QB-02C completion
  // report for the documented limitation). Auto-selected when a
  // commercial template is applied; the consultant can also change it
  // manually via the dropdown in the Service and scope section.
  const [deliveryCode, setDeliveryCode] = useState(DEFAULT_DELIVERY_CODE);
  // QB-02C — Recommendation Preview drawer state.
  const [previewOpen, setPreviewOpen] = useState(false);

  // Sections default: first four essential sections open, deliverables +
  // notes closed. User can toggle freely.
  const [openSections, setOpenSections] = useState({
    identifiers: true,
    provider: true,
    service: true,
    pricing: true,
    deliverables: false,
    notes: false
  });

  useEffect(() => {
    pricingApi.list().then(setPriceItems).catch(() => setPriceItems([]));
  }, []);

  useEffect(() => {
    if (quote) {
      const next = { ...emptyDraft };
      Object.keys(emptyDraft).forEach((k) => {
        if (quote[k] !== undefined && quote[k] !== null) next[k] = String(quote[k]);
      });
      setDraft(next);
      setPreviewData(null);
      setErrorsByField({});
      // If editing an existing quote, open sections that contain data so
      // the operator sees everything they already entered at a glance.
      const nextOpen = { ...openSections };
      for (const [section, fields] of Object.entries(SECTION_FIELDS)) {
        if (fields.some((f) => next[f] && String(next[f]).trim() !== "")) {
          nextOpen[section] = true;
        }
      }
      setOpenSections(nextOpen);
      // Detect legacy urgency / size values not in the canonical list so we
      // switch to custom-text mode instead of silently blanking them.
      const isCanonicalUrgency = URGENCY_CANONICAL.some((o) => o.value === next.urgency);
      setUrgencyCustomMode(Boolean(next.urgency) && !isCanonicalUrgency);
      const isCanonicalSize = PROVIDER_SIZE_CANONICAL.some((o) => o.value === next.providerSizeOrCohort);
      setSizeCustomMode(Boolean(next.providerSizeOrCohort) && !isCanonicalSize);
      // QB-02B — an existing draft's non-empty recommendable fields are
      // treated as consultant-authored (provenance cannot be recovered
      // from the persisted document; safer to protect them).
      const seeded = new Set();
      for (const f of ["assessmentScope","documentReviewScope","estimatedTimeframe","inclusions","exclusions","evidenceRequiredFromProvider","deliverables","additionalProfessionalReferrals","internalCommercialNotes"]) {
        if (next[f] && String(next[f]).trim() !== "") seeded.add(f);
      }
      setTouchedFields(seeded);
      setPromptChecks({});
      // QB-02C — reset delivery method to the SBC-001 default when loading
      // a different quote (delivery method is a session-only presentation
      // control; see completion report).
      setDeliveryCode(DEFAULT_DELIVERY_CODE);
    } else {
      setDraft(emptyDraft);
      setPreviewData(null);
      setErrorsByField({});
      setUrgencyCustomMode(false);
      setSizeCustomMode(false);
      setTouchedFields(new Set());
      setPromptChecks({});
      setDeliveryCode(DEFAULT_DELIVERY_CODE);
      setOpenSections({
        identifiers: true,
        provider: true,
        service: true,
        pricing: true,
        deliverables: false,
        notes: false
      });
    }
    // openSections intentionally excluded: this effect resets sections when
    // the `quote` prop changes; including openSections would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote]);

  const set = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    // QB-02B — a direct edit by the consultant marks the field as
    // consultant-authored so the recommendation engine never silently
    // overwrites it. Only recommendable fields need to be tracked; other
    // fields (provider details, pricing amounts, dates, etc.) are
    // untouched by recommendations by design.
    if (Object.prototype.hasOwnProperty.call(RECOMMENDABLE_FIELD_LABELS, key)) {
      setTouchedFields((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    }
    // Clear a field-level error as soon as the operator edits it.
    if (errorsByField[key]) {
      setErrorsByField((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // QB-02B — apply a service profile (or template) recommendation to the
  // current draft. Fields the consultant has already edited are protected
  // unless `forceOverwrite` is true (used only when the consultant
  // explicitly picks the "regenerate commercial content" option).
  const applyRecommendation = (recommendation, { forceOverwrite = false, sourceLabel = "" } = {}) => {
    if (!recommendation) return;
    setDraft((d) => {
      const { nextDraft, appliedFields, skippedFields } = applyRecommendationToDraft(
        d,
        recommendation,
        { touchedFields, forceOverwrite }
      );
      const suffix = skippedFields.length
        ? ` (${skippedFields.length} consultant-edited field${skippedFields.length === 1 ? "" : "s"} preserved)`
        : "";
      const prefix = sourceLabel ? `${sourceLabel} — ` : "";
      if (appliedFields.length === 0 && skippedFields.length > 0) {
        toast.info(`${prefix}Consultant-edited content preserved. No fields changed.`);
      } else if (appliedFields.length > 0) {
        toast.success(`${prefix}Recommendations applied to ${appliedFields.length} field${appliedFields.length === 1 ? "" : "s"}${suffix}.`);
      }
      // Auto-open sections that received new content so the consultant sees
      // what was populated.
      setOpenSections((prev) => {
        const nxt = { ...prev };
        appliedFields.forEach((f) => {
          const sect = sectionForField(f);
          if (sect) nxt[sect] = true;
        });
        return nxt;
      });
      return nextDraft;
    });
  };

  const applyServiceDefaults = (code) => {
    const item = priceItems.find((p) => p.code === code);
    if (!item) return;
    setDraft((d) => ({
      ...d,
      selectedServiceCode: code,
      pricingBasis: item.basis,
      pricingRangeMin: String(item.minPrice),
      pricingRangeMax: String(item.maxPrice)
    }));
  };

  const changeServiceKeepPricing = (code) => {
    setDraft((d) => ({ ...d, selectedServiceCode: code }));
  };

  const handleServiceSelect = (code) => {
    if (!code) {
      setDraft((d) => ({ ...d, selectedServiceCode: "" }));
      return;
    }
    const currentCode = draft.selectedServiceCode;
    const hasPricing =
      String(draft.pricingRangeMin || "").trim() !== "" ||
      String(draft.pricingRangeMax || "").trim() !== "";
    if (!currentCode && !hasPricing) {
      // QB-02B — first service selection: apply pricing defaults AND the
      // service profile recommendations in one step. Consultant-touched
      // fields (there shouldn't be any at this point on a fresh draft, but
      // the guard is respected regardless) remain protected.
      applyServiceDefaults(code);
      const profile = getServiceProfile(code);
      if (profile) {
        applyRecommendation(profile, { sourceLabel: `Service profile: ${profile.label}` });
      }
      return;
    }
    if (currentCode === code) return;
    setPendingServiceCode(code);
  };

  const dialogTargetService = pendingServiceCode
    ? priceItems.find((p) => p.code === pendingServiceCode)
    : null;

  const confirmApplyDefaults = () => {
    if (pendingServiceCode) {
      applyServiceDefaults(pendingServiceCode);
      // Chain to the commercial-content decision so the consultant can also
      // regenerate scope / inclusions / exclusions / deliverables etc.
      setPendingRegen(pendingServiceCode);
    }
    setPendingServiceCode(null);
  };

  const confirmKeepPricing = () => {
    if (pendingServiceCode) {
      changeServiceKeepPricing(pendingServiceCode);
      setPendingRegen(pendingServiceCode);
    }
    setPendingServiceCode(null);
  };

  const cancelServiceChange = () => setPendingServiceCode(null);

  // QB-02B — commercial-content regeneration handlers.
  const regenTargetProfile = pendingRegen ? getServiceProfile(pendingRegen) : null;
  const regenTargetService = pendingRegen
    ? priceItems.find((p) => p.code === pendingRegen)
    : null;
  const regenTouchedFieldsList = pendingRegen
    ? Array.from(touchedFields).filter((f) =>
        Object.prototype.hasOwnProperty.call(RECOMMENDABLE_FIELD_LABELS, f)
      )
    : [];

  const applyRegenPreservingEdits = () => {
    if (regenTargetProfile) {
      applyRecommendation(regenTargetProfile, {
        sourceLabel: `Service profile: ${regenTargetProfile.label}`
      });
    }
    setPendingRegen(null);
  };

  const applyRegenOverwriteAll = () => {
    if (regenTargetProfile) {
      // The consultant has explicitly asked to overwrite touched fields too.
      // Clear the touched-fields set so future recommendations are protected
      // from the point of view of the newly generated content going forward.
      applyRecommendation(regenTargetProfile, {
        forceOverwrite: true,
        sourceLabel: `Service profile: ${regenTargetProfile.label}`
      });
      setTouchedFields(new Set());
    }
    setPendingRegen(null);
  };

  const skipRegen = () => setPendingRegen(null);

  // QB-02B — templates (commercial engagement modes) can be applied
  // independently at any time. `templateId` is a plain string.
  const handleTemplateApply = (templateId) => {
    const tpl = getCommercialTemplate(templateId);
    if (!tpl) return;
    applyRecommendation(tpl, { sourceLabel: `Template: ${tpl.label}` });
    // QB-02C — auto-align the SBC-001 delivery method when a template
    // clearly implies one. Consultant can still override manually.
    const mappedDelivery = TEMPLATE_TO_DELIVERY_CODE[templateId];
    if (mappedDelivery) setDeliveryCode(mappedDelivery);
  };

  // QB-02B — a consultant may explicitly request full regeneration of the
  // recommendation content for the currently-selected service, discarding
  // their edits. This is a manual, opt-in action from the Service section.
  const handleRegenerateCurrent = () => {
    const code = draft.selectedServiceCode;
    if (!code) return;
    setPendingRegen(code);
  };

  // QB-02C — Recommendation Preview drawer. Read-only. Opening does not
  // mutate any quote state.
  const openRecommendationPreview = () => {
    if (!draft.selectedServiceCode) {
      toast.info("Select a service to view its recommendations.");
      return;
    }
    setPreviewOpen(true);
  };

  // QB-02C — Apply Recommendations from the preview. Delegates to the
  // existing overwrite-aware regen flow so consultant edits are protected.
  const applyFromPreview = () => {
    setPreviewOpen(false);
    if (!draft.selectedServiceCode) return;
    setPendingRegen(draft.selectedServiceCode);
  };

  const openSectionsForErrors = (errs) => {
    setOpenSections((prev) => {
      const next = { ...prev };
      Object.keys(errs).forEach((fieldKey) => {
        const s = sectionForField(fieldKey);
        if (s) next[s] = true;
      });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setErrorsByField({});
    try {
      const payload = toPayload(draft);
      let saved;
      if (quote?.id) {
        saved = await quoteApi.patch(quote.id, payload);
        toast.success(`Quote updated (v${saved.version}).`);
      } else {
        saved = await quoteApi.create(payload);
        toast.success("Draft quote created.");
      }
      onSaved?.(saved);
    } catch (e) {
      let message = e?.message || "Save failed.";
      const nextErrors = {};
      if (e?.body) {
        try {
          const parsed = JSON.parse(e.body);
          if (Array.isArray(parsed?.detail)) {
            const parts = parsed.detail
              .map((d) => {
                const rawLoc = Array.isArray(d.loc) ? d.loc.slice(1) : [];
                const field = rawLoc.join(".");
                if (field && Object.prototype.hasOwnProperty.call(FIELD_LABELS, field)) {
                  nextErrors[field] = d.msg;
                }
                const label = field ? (FIELD_LABELS[field] || field) : "";
                return label ? `${label}: ${d.msg}` : d.msg;
              })
              .filter(Boolean);
            if (parts.length) message = `Validation error — ${parts.join("; ")}`;
          } else if (typeof parsed?.detail === "string") {
            message = parsed.detail;
          }
        } catch {
          /* fall through */
        }
      }
      if (e?.status === 404 && quote?.id) {
        message =
          "This quote no longer exists on the server. Click 'New quote' to start a fresh draft.";
      }
      if (Object.keys(nextErrors).length) {
        setErrorsByField(nextErrors);
        openSectionsForErrors(nextErrors);
      }
      toast.error(message, { duration: 10000 });
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (nextStatus) => {
    if (!quote?.id) {
      toast.info("Save the draft first.");
      return;
    }
    try {
      const updated = await quoteApi.patch(quote.id, { status: nextStatus });
      toast.success(`Status → ${updated.status}.`);
      onSaved?.(updated);
    } catch (e) {
      const msg =
        e.status === 422 ? "Owner approval required before this status." : "Status change failed.";
      toast.error(msg);
    }
  };

  const approveByOwner = async () => {
    if (!quote?.id) return;
    try {
      const updated = await quoteApi.patch(quote.id, { ownerApproved: true });
      toast.success("Owner approval recorded. Quote may now move to Approved / Issued.");
      onSaved?.(updated);
    } catch {
      toast.error("Approval failed.");
    }
  };

  const loadPreview = async () => {
    if (!quote?.id) {
      toast.info("Save the draft first to preview the client-facing quote.");
      return;
    }
    setLoadingPreview(true);
    try {
      const data = await quoteApi.preview(quote.id);
      setPreviewData(data);
    } catch {
      toast.error("Preview failed.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const copyInvoicePreparation = () => {
    const d = draft;
    const price = d.proposedFinalPrice
      ? `$${formatAudNumber(d.proposedFinalPrice)}`
      : (d.pricingRangeMin
          ? `$${formatAudNumber(d.pricingRangeMin)}\u2013$${formatAudNumber(d.pricingRangeMax)}`
          : "TBC — confirm before issuing invoice");
    const gst = d.gstSetting && d.gstSetting !== "UNCONFIRMED"
      ? d.gstSetting.toLowerCase().replace(/_/g, " ")
      : "to be confirmed by the owner before issuing";
    const dep = d.depositAmount ? `$${formatAudNumber(d.depositAmount)}` : "not required";
    const balance = d.balanceTerms ? d.balanceTerms : "balance due on completion, per manually issued invoice";
    const lines = [];
    lines.push("INVOICE PREPARATION \u2014 INTERNAL ADMIN NOTE");
    lines.push("==================================================");
    lines.push("This is an admin preparation aid. It is NOT a legal invoice, tax invoice or receipt.");
    lines.push("The manually issued invoice remains the source of truth.");
    lines.push("");
    lines.push("Prepared for:");
    lines.push(`  Legal entity: ${d.providerLegalEntity || "\u2014"}`);
    if (d.tradingName) lines.push(`  Trading as:   ${d.tradingName}`);
    if (d.abn) lines.push(`  Provider ABN: ${d.abn}`);
    if (d.contactPerson) lines.push(`  Contact:      ${d.contactPerson}`);
    if (d.contactEmail) lines.push(`  Email:        ${d.contactEmail}`);
    lines.push("");
    lines.push("Reference:");
    if (d.quoteNumber) lines.push(`  Quote number: ${d.quoteNumber}`);
    if (d.dateCreated) lines.push(`  Quote issued: ${formatDateDisplay(d.dateCreated)}`);
    if (d.expiryDate) lines.push(`  Valid until:  ${formatDateDisplay(d.expiryDate)}`);
    lines.push("  Manual invoice number: __________  (assign at time of issue)");
    lines.push("  Invoice issue date:    __________");
    lines.push("  Payment due date:      __________");
    lines.push("");
    lines.push("Service and scope:");
    if (d.selectedServiceCode) lines.push(`  Service code: ${d.selectedServiceCode}`);
    if (d.assessmentScope) lines.push(`  Scope: ${d.assessmentScope}`);
    if (d.deliverables) lines.push(`  Deliverables: ${d.deliverables}`);
    if (d.inclusions) lines.push(`  Inclusions:   ${d.inclusions}`);
    if (d.exclusions) lines.push(`  Exclusions:   ${d.exclusions}`);
    lines.push("");
    lines.push("Amounts:");
    lines.push(`  Fee:      ${price}`);
    lines.push(`  GST:      ${gst}`);
    lines.push(`  Deposit:  ${dep}`);
    lines.push(`  Balance:  ${balance}`);
    lines.push("");
    lines.push("Payment status (update manually as it changes):");
    lines.push("  [ ] Not yet issued");
    lines.push("  [ ] Issued manually");
    lines.push("  [ ] Deposit received");
    lines.push("  [ ] Paid in full");
    lines.push("  [ ] Overdue");
    lines.push("  [ ] Waived (owner-approved)");
    lines.push("");
    lines.push("Governance:");
    lines.push(`  ${DEPOSIT_GOVERNANCE_CAVEAT}`);
    lines.push(`  ${NON_PAYMENT_NOTE}`);
    lines.push("");
    lines.push("Operator:");
    lines.push(`  ${OPERATOR_LEGAL_LINE}`);
    lines.push("");
    lines.push("Payment method note:");
    lines.push("  Bank transfer / EFT to the account nominated on the manually issued invoice.");
    lines.push("  Reconciliation is performed by the owner. No card or banking credentials are stored in this system.");
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Invoice preparation text copied (admin-only, not a legal invoice).");
  };

  const copyPreview = () => {
    if (!previewData) return;
    const lines = [];
    lines.push("PROFESSIONAL SERVICES QUOTE");
    lines.push("==================================================");
    if (previewData.quoteNumber) lines.push(`Quote no:    ${previewData.quoteNumber}`);
    if (previewData.version) lines.push(`Version:     ${previewData.version}`);
    if (previewData.dateCreated) lines.push(`Issued:      ${formatDateDisplay(previewData.dateCreated)}`);
    if (previewData.validUntil) lines.push(`Valid until: ${formatDateDisplay(previewData.validUntil)}`);
    lines.push("");
    if (previewData.providerLegalEntity) lines.push(`Prepared for: ${previewData.providerLegalEntity}`);
    if (previewData.tradingName) lines.push(`Trading as:  ${previewData.tradingName}`);
    if (previewData.abn) lines.push(`ABN:         ${previewData.abn}`);
    lines.push("");
    if (previewData.assessmentScope) lines.push(`SCOPE: ${previewData.assessmentScope}`);
    if (previewData.deliverables) lines.push(`DELIVERABLES: ${previewData.deliverables}`);
    if (previewData.inclusions) lines.push(`INCLUSIONS: ${previewData.inclusions}`);
    if (previewData.exclusions) lines.push(`EXCLUSIONS: ${previewData.exclusions}`);
    if (previewData.estimatedTimeframe) lines.push(`TIMEFRAME: ${previewData.estimatedTimeframe}`);
    lines.push("");
    lines.push("FEE:");
    if (previewData.proposedFinalPrice) {
      lines.push(`  Proposed fee: $${formatAudNumber(previewData.proposedFinalPrice)}`);
    } else if (previewData.pricingRangeMin) {
      lines.push(
        `  Fee range: $${formatAudNumber(previewData.pricingRangeMin)} – $${formatAudNumber(previewData.pricingRangeMax)}`
      );
    }
    if (previewData.gstSetting && previewData.gstSetting !== "UNCONFIRMED") {
      lines.push(`  GST: ${previewData.gstSetting.toLowerCase()}`);
    } else {
      lines.push("  GST: to be confirmed by the owner before invoicing");
    }
    if (previewData.depositAmount) lines.push(`  Deposit: $${formatAudNumber(previewData.depositAmount)}`);
    if (previewData.balanceTerms) lines.push(`  Balance terms: ${previewData.balanceTerms}`);
    if (previewData.clientFacingNotes) {
      lines.push("");
      lines.push("NOTES:");
      lines.push(previewData.clientFacingNotes);
    }
    lines.push("");
    lines.push("QUOTE VALIDITY:");
    lines.push(
      previewData.validUntil
        ? `  This quote is valid until ${formatDateDisplay(previewData.validUntil)}. If not accepted by that date, please request a fresh quote.`
        : `  ${VALIDITY_FALLBACK}`
    );
    lines.push("");
    lines.push("ACCEPTANCE:");
    lines.push(`  ${QUOTE_ACCEPTANCE_INSTRUCTION}`);
    lines.push("");
    lines.push("PAYMENT TERMS:");
    lines.push(`  ${PAYMENT_TERMS_LINE}`);
    lines.push(`  ${DEPOSIT_GOVERNANCE_CAVEAT}`);
    lines.push(`  ${NON_PAYMENT_NOTE}`);
    lines.push("");
    lines.push("OPERATOR:");
    lines.push(`  ${OPERATOR_LEGAL_LINE}`);
    lines.push("");
    lines.push("BOUNDARY STATEMENT:");
    lines.push(previewData.boundaryStatement);
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Client-facing quote copied to clipboard.");
  };

  const toggleSection = (name) =>
    setOpenSections((prev) => ({ ...prev, [name]: !prev[name] }));

  // Section metadata for the collapsible headers.
  const sectionMeta = useMemo(() => {
    const meta = {};
    for (const [section, fields] of Object.entries(SECTION_FIELDS)) {
      const filled = fields.filter((f) => String(draft[f] || "").trim() !== "").length;
      const hasError = fields.some((f) => Boolean(errorsByField[f]));
      meta[section] = { filled, total: fields.length, hasError };
    }
    return meta;
  }, [draft, errorsByField]);

  const currentStatus = quote?.status || "DRAFT";
  const currentStatusLabel =
    QUOTE_STATUS_OPTIONS.find((s) => s.value === currentStatus)?.label || currentStatus;

  return (
    <div className="space-y-6 pb-24" data-testid="quote-builder">
      {quote && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-sm border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
          data-testid="qb-quote-meta"
        >
          <span className="font-mono-label">Quote id: {quote.id}</span>
          <span>Version: {quote.version}</span>
          <span>
            Status:{" "}
            <span className="font-medium text-foreground">{currentStatusLabel}</span>
          </span>
          <span className="inline-flex items-center gap-1">
            Owner approved:{" "}
            {quote.ownerApproved ? (
              <span className="inline-flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="h-3 w-3" /> yes
              </span>
            ) : (
              "no"
            )}
          </span>
        </div>
      )}

      {Object.keys(errorsByField).length > 0 && (
        <div
          className="flex items-start gap-2 rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-900"
          role="alert"
          data-testid="qb-error-summary"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Some fields need attention before saving:</p>
            <ul className="mt-1 list-disc pl-5">
              {Object.entries(errorsByField).map(([field, msg]) => (
                <li key={field}>
                  <span className="font-medium">{FIELD_LABELS[field] || field}</span>: {msg}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <SectionShell
        name="identifiers"
        title="Quote identifiers"
        description="Internal reference number and validity dates."
        open={openSections.identifiers}
        onToggle={() => toggleSection("identifiers")}
        meta={sectionMeta.identifiers}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <TextField
            id="qb-quote-number"
            label="Quote number"
            value={draft.quoteNumber}
            onChange={(v) => set("quoteNumber", v)}
            placeholder="e.g. SBC-2026-0001"
            help="A unique, human-readable identifier the consultant assigns for tracking."
            error={errorsByField.quoteNumber}
            testid="qb-quote-number"
          />
          <DateField
            id="qb-date-created"
            label="Date created"
            value={draft.dateCreated}
            onChange={(iso) => set("dateCreated", iso)}
            help="When the quote is drafted. Displayed as DD/MM/YYYY."
            error={errorsByField.dateCreated}
            testid="qb-date-created"
          />
          <DateField
            id="qb-expiry-date"
            label="Expiry date"
            value={draft.expiryDate}
            onChange={(iso) => set("expiryDate", iso)}
            help="Last date the client can accept this quote at the stated fee."
            error={errorsByField.expiryDate}
            testid="qb-expiry-date"
          />
        </div>
      </SectionShell>

      <SectionShell
        name="provider"
        title="Provider details"
        description="Who the quote is prepared for."
        open={openSections.provider}
        onToggle={() => toggleSection("provider")}
        meta={sectionMeta.provider}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextField
            id="qb-provider-legal"
            label="Provider legal entity"
            value={draft.providerLegalEntity}
            onChange={(v) => set("providerLegalEntity", v)}
            placeholder="Full registered name"
            help="Legal name as registered on ASIC / ABR."
            error={errorsByField.providerLegalEntity}
            testid="qb-provider-legal"
          />
          <TextField
            id="qb-trading-name"
            label="Trading name"
            value={draft.tradingName}
            onChange={(v) => set("tradingName", v)}
            placeholder="Optional trading-as name"
            help="Leave blank if the trading name is the same as the legal entity."
            error={errorsByField.tradingName}
          />
          <TextField
            id="qb-abn"
            label="ABN"
            value={draft.abn}
            onChange={(v) => set("abn", v)}
            placeholder="11 222 333 444 (11 digits, spaces optional)"
            help="Australian Business Number. Not validated against ABR."
            error={errorsByField.abn}
          />
          <TextField
            id="qb-contact-person"
            label="Contact person"
            value={draft.contactPerson}
            onChange={(v) => set("contactPerson", v)}
            placeholder="Primary contact for this engagement"
            help="Name of the person the consultant will correspond with."
            error={errorsByField.contactPerson}
          />
          <TextField
            id="qb-contact-email"
            label="Contact email"
            value={draft.contactEmail}
            onChange={(v) => set("contactEmail", v)}
            placeholder="name@example.com.au"
            help="Used only for correspondence about this quote."
            error={errorsByField.contactEmail}
            type="email"
          />
          <TextField
            id="qb-contact-phone"
            label="Contact phone"
            value={draft.contactPhone}
            onChange={(v) => set("contactPhone", v)}
            placeholder="e.g. 0412 345 678"
            help="Optional. Preferred callback number."
            error={errorsByField.contactPhone}
            type="tel"
          />
          <SelectWithOtherField
            id="qb-provider-size"
            label="Provider size / cohort"
            value={draft.providerSizeOrCohort}
            onChange={(v) => set("providerSizeOrCohort", v)}
            options={PROVIDER_SIZE_CANONICAL}
            customMode={sizeCustomMode}
            setCustomMode={setSizeCustomMode}
            help="Rough size — informs scope and effort estimation."
            error={errorsByField.providerSizeOrCohort}
          />
          <SelectWithOtherField
            id="qb-urgency"
            label="Urgency"
            value={draft.urgency}
            onChange={(v) => set("urgency", v)}
            options={URGENCY_CANONICAL}
            customMode={urgencyCustomMode}
            setCustomMode={setUrgencyCustomMode}
            help="Reflects turnaround expectations only. Does not change price."
            error={errorsByField.urgency}
          />
        </div>
      </SectionShell>

      <SectionShell
        name="service"
        title="Service and scope"
        description="What the consultancy is being engaged to do."
        open={openSections.service}
        onToggle={() => toggleSection("service")}
        meta={sectionMeta.service}
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="qb-service-select"
              className="block text-xs uppercase tracking-[0.16em] text-muted-foreground"
            >
              Selected service
            </label>
            <select
              id="qb-service-select"
              value={draft.selectedServiceCode}
              onChange={(e) => handleServiceSelect(e.target.value)}
              className="mt-1 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="qb-service-select"
              aria-describedby="qb-service-help"
              aria-invalid={Boolean(errorsByField.selectedServiceCode) || undefined}
            >
              <option value="">— pick a service —</option>
              {priceItems.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label} — ${formatAudNumber(String(p.minPrice))}–${formatAudNumber(String(p.maxPrice))} {p.unit}
                </option>
              ))}
            </select>
            <p id="qb-service-help" className="mt-1 text-xs text-muted-foreground">
              Pricing basis, minimum and maximum will pre-fill from this service.
              Recommended commercial content will also pre-fill on first selection.
              Changing the service later will ask before it replaces existing pricing
              or recommended content.
            </p>
            {errorsByField.selectedServiceCode && (
              <p className="mt-1 text-xs text-red-700" role="alert">
                {errorsByField.selectedServiceCode}
              </p>
            )}
          </div>

          {/* QB-02B — active service-profile summary and regenerate control */}
          {(() => {
            const activeProfile = getServiceProfile(draft.selectedServiceCode);
            if (!activeProfile) return null;
            return (
              <div
                className="rounded-sm border border-emerald-200 bg-emerald-50/50 p-3 text-xs text-foreground/85"
                data-testid="qb-service-profile-summary"
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="eyebrow text-emerald-900">
                    Recommended commercial profile · {activeProfile.label}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={openRecommendationPreview}
                      className="rounded-sm"
                      data-testid="qb-open-recommendation-preview"
                      title="Read-only preview of the service's recommendation content."
                    >
                      Recommendation preview
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleRegenerateCurrent}
                      className="rounded-sm"
                      data-testid="qb-regenerate-recommendations"
                      title="Reload the recommendation content for the current service."
                    >
                      Regenerate recommendations
                    </Button>
                  </div>
                </div>
                <dl className="grid grid-cols-1 gap-1 md:grid-cols-2">
                  {activeProfile.recommendedPricingModel && (
                    <div className="flex gap-2">
                      <dt className="min-w-[9.5rem] text-muted-foreground">Pricing model</dt>
                      <dd>{activeProfile.recommendedPricingModel}</dd>
                    </div>
                  )}
                  {activeProfile.typicalDuration && (
                    <div className="flex gap-2">
                      <dt className="min-w-[9.5rem] text-muted-foreground">Typical duration</dt>
                      <dd>{activeProfile.typicalDuration}</dd>
                    </div>
                  )}
                  {activeProfile.typicalEffortHours && (
                    <div className="flex gap-2">
                      <dt className="min-w-[9.5rem] text-muted-foreground">Typical effort</dt>
                      <dd>{activeProfile.typicalEffortHours}</dd>
                    </div>
                  )}
                  {activeProfile.suggestedFeePresentation && (
                    <div className="flex gap-2">
                      <dt className="min-w-[9.5rem] text-muted-foreground">Fee presentation</dt>
                      <dd>{activeProfile.suggestedFeePresentation}</dd>
                    </div>
                  )}
                </dl>
                <p className="mt-2 text-[11px] italic text-muted-foreground">
                  Guidance only. All figures, wording and calculations are decided by the consultant.
                </p>
              </div>
            );
          })()}

          {/* QB-02B — commercial-template loader */}
          <div className="rounded-sm border border-border bg-muted/30 p-3">
            <label
              htmlFor="qb-template-select"
              className="block text-xs uppercase tracking-[0.16em] text-muted-foreground"
            >
              Load a commercial template
            </label>
            <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                id="qb-template-select"
                defaultValue=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  handleTemplateApply(id);
                  e.target.value = "";
                }}
                className="w-full rounded-sm border border-border bg-card px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-1"
                data-testid="qb-template-select"
              >
                <option value="">— pick a template to apply —</option>
                {Object.entries(COMMERCIAL_TEMPLATES).map(([id, tpl]) => (
                  <option key={id} value={id}>{tpl.label}</option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Templates overlay engagement-mode recommendations onto the current draft.
              Consultant-edited fields are preserved. Pricing is never changed.
            </p>
          </div>

          {/* QB-02C — SBC-001 §1.9 Delivery Method (session-only presentation control) */}
          <div>
            <label
              htmlFor="qb-delivery-method"
              className="block text-xs uppercase tracking-[0.16em] text-muted-foreground"
            >
              Delivery method (client-facing presentation)
            </label>
            <select
              id="qb-delivery-method"
              value={deliveryCode}
              onChange={(e) => setDeliveryCode(e.target.value)}
              className="mt-1 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="qb-delivery-method"
            >
              {DELIVERY_METHODS.map((m) => (
                <option key={m.code} value={m.code}>{m.code} · {m.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Shown in the client-facing preview. Auto-aligned when a commercial
              template is applied; adjust here if the engagement mode differs.
              Session-only — reselect after reopening a saved draft.
            </p>
          </div>
          <TextAreaField
            id="qb-assessment-scope"
            label="Assessment scope"
            value={draft.assessmentScope}
            onChange={(v) => set("assessmentScope", v)}
            placeholder="e.g. R.E.A.D.Y. gap analysis across governance, incident management and safeguarding for a single-site SIL provider."
            help="Plain-language description of what will be assessed. Appears on the client quote."
            error={errorsByField.assessmentScope}
            edited={touchedFields.has("assessmentScope")}
            testid="qb-assessment-scope"
          />
          <TextAreaField
            id="qb-document-review-scope"
            label="Document review scope"
            value={draft.documentReviewScope}
            onChange={(v) => set("documentReviewScope", v)}
            placeholder="e.g. Policies, procedures, evidence packs, incident register."
            help="What documents / evidence the consultancy will review. Appears on the client quote."
            error={errorsByField.documentReviewScope}
            edited={touchedFields.has("documentReviewScope")}
          />

          {/* QB-02B — consultant prompts (session-only decision support) */}
          <div className="rounded-sm border border-dashed border-border bg-card/60 p-3" data-testid="qb-consultant-prompts">
            <p className="eyebrow text-foreground">Commercial prompts</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Reminders only. Ticking a box is a mental note for this session — nothing is saved or transmitted.
            </p>
            <ul className="mt-2 space-y-1.5">
              {CONSULTANT_PROMPTS.map((p) => (
                <li key={p.id} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    id={`qb-prompt-${p.id}`}
                    checked={Boolean(promptChecks[p.id])}
                    onChange={(e) => setPromptChecks((prev) => ({ ...prev, [p.id]: e.target.checked }))}
                    className="mt-1 h-3.5 w-3.5 rounded-sm border-border"
                    data-testid={`qb-prompt-${p.id}`}
                  />
                  <label htmlFor={`qb-prompt-${p.id}`} className="text-foreground/85">
                    {p.text}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SectionShell>

      <SectionShell
        name="pricing"
        title="Pricing and GST"
        description="All amounts are AUD. Formatting is presentation-only — the numeric value is what is saved."
        open={openSections.pricing}
        onToggle={() => toggleSection("pricing")}
        meta={sectionMeta.pricing}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <NumericField
            id="qb-estimated-hours"
            label="Estimated hours"
            value={draft.estimatedHours}
            onChange={(v) => set("estimatedHours", v)}
            placeholder="e.g. 12"
            help="Consultant's internal effort estimate. Not shown on the client quote."
            error={errorsByField.estimatedHours}
          />
          <SelectField
            id="qb-pricing-basis"
            label="Pricing basis"
            value={draft.pricingBasis}
            onChange={(v) => set("pricingBasis", v)}
            options={PRICING_BASIS_OPTIONS}
            help="Fixed range is used for scoped engagements; hourly is used for open-ended work."
            error={errorsByField.pricingBasis}
          />
          <div className="hidden md:block" aria-hidden="true" />
          <CurrencyField
            id="qb-pricing-min"
            label="Fee range minimum (AUD)"
            value={draft.pricingRangeMin}
            onChange={(v) => set("pricingRangeMin", v)}
            placeholder="e.g. 1500"
            help="Lower bound of the quoted range."
            error={errorsByField.pricingRangeMin}
            testid="qb-pricing-min"
          />
          <CurrencyField
            id="qb-pricing-max"
            label="Fee range maximum (AUD)"
            value={draft.pricingRangeMax}
            onChange={(v) => set("pricingRangeMax", v)}
            placeholder="e.g. 2500"
            help="Upper bound of the quoted range."
            error={errorsByField.pricingRangeMax}
            testid="qb-pricing-max"
          />
          <CurrencyField
            id="qb-final-price"
            label="Proposed final fee (AUD)"
            value={draft.proposedFinalPrice}
            onChange={(v) => set("proposedFinalPrice", v)}
            placeholder="e.g. 2000"
            help="Single-figure fee. Leave blank to quote the range only."
            error={errorsByField.proposedFinalPrice}
            testid="qb-final-price"
          />
          <SelectField
            id="qb-gst"
            label="GST setting"
            value={draft.gstSetting}
            onChange={(v) => set("gstSetting", v)}
            options={GST_SETTING_OPTIONS}
            help="GST treatment is confirmed on the manually issued invoice."
            error={errorsByField.gstSetting}
          />
          <CurrencyField
            id="qb-deposit"
            label="Deposit (AUD)"
            value={draft.depositAmount}
            onChange={(v) => set("depositAmount", v)}
            placeholder="e.g. 500"
            help="Optional up-front amount required before work commences."
            error={errorsByField.depositAmount}
          />
          <TextField
            id="qb-balance-terms"
            label="Balance terms"
            value={draft.balanceTerms}
            onChange={(v) => set("balanceTerms", v)}
            placeholder="e.g. Balance due on delivery of the readiness report."
            help="Plain-language wording. Shown on the client quote."
            error={errorsByField.balanceTerms}
          />
        </div>
      </SectionShell>

      <SectionShell
        name="deliverables"
        title="Deliverables and timeframe"
        description="What the client receives and by when."
        open={openSections.deliverables}
        onToggle={() => toggleSection("deliverables")}
        meta={sectionMeta.deliverables}
      >
        <div className="space-y-4">
          <TextAreaField
            id="qb-inclusions"
            label="Inclusions"
            value={draft.inclusions}
            onChange={(v) => set("inclusions", v)}
            placeholder="What is expressly included in this fee."
            help="Bulleted or comma-separated list. Appears verbatim on the client quote."
            error={errorsByField.inclusions}
            edited={touchedFields.has("inclusions")}
            testid="qb-inclusions"
          />
          <TextAreaField
            id="qb-exclusions"
            label="Exclusions"
            value={draft.exclusions}
            onChange={(v) => set("exclusions", v)}
            placeholder="What is NOT covered by this fee (e.g. re-audits, travel, ad-hoc calls)."
            help="Protects both parties. Appears verbatim on the client quote."
            error={errorsByField.exclusions}
            edited={touchedFields.has("exclusions")}
            testid="qb-exclusions"
          />
          <TextAreaField
            id="qb-evidence-required"
            label="Evidence required from provider"
            value={draft.evidenceRequiredFromProvider}
            onChange={(v) => set("evidenceRequiredFromProvider", v)}
            placeholder="e.g. Current policies, latest incident register, staff training records."
            help="Tells the client what to have ready before the engagement starts."
            error={errorsByField.evidenceRequiredFromProvider}
            edited={touchedFields.has("evidenceRequiredFromProvider")}
          />
          <TextAreaField
            id="qb-deliverables"
            label="Deliverables"
            value={draft.deliverables}
            onChange={(v) => set("deliverables", v)}
            placeholder="e.g. Written readiness report, 30/60/90 action plan, one debrief call."
            help="What tangible outputs the client receives."
            error={errorsByField.deliverables}
            edited={touchedFields.has("deliverables")}
            testid="qb-deliverables"
          />
          <TextField
            id="qb-timeframe"
            label="Estimated timeframe"
            value={draft.estimatedTimeframe}
            onChange={(v) => set("estimatedTimeframe", v)}
            placeholder="e.g. 3 weeks from receipt of complete evidence pack."
            help="Plain language. Contingencies (e.g. dependent on evidence being provided) can be stated here."
            error={errorsByField.estimatedTimeframe}
            edited={touchedFields.has("estimatedTimeframe")}
          />
          <TextAreaField
            id="qb-referrals"
            label="Additional professional referrals"
            value={draft.additionalProfessionalReferrals}
            onChange={(v) => set("additionalProfessionalReferrals", v)}
            placeholder="e.g. Areas flagged for legal, accounting or clinical review outside the consultancy's scope."
            help="Explicitly cross-references matters that lie outside this readiness engagement."
            error={errorsByField.additionalProfessionalReferrals}
            edited={touchedFields.has("additionalProfessionalReferrals")}
          />
        </div>
      </SectionShell>

      <SectionShell
        name="notes"
        title="Notes"
        description="Internal notes stay internal. Client-facing notes appear on the quote."
        open={openSections.notes}
        onToggle={() => toggleSection("notes")}
        meta={sectionMeta.notes}
      >
        <div className="space-y-4">
          <div className="rounded-sm border-2 border-amber-300 bg-amber-50 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-amber-900">
              <ShieldCheck className="h-4 w-4" />
              Internal only — never shown to the client
            </div>
            <TextAreaField
              id="qb-internal-notes"
              label="Internal commercial notes"
              value={draft.internalCommercialNotes}
              onChange={(v) => set("internalCommercialNotes", v)}
              placeholder="e.g. Margin considerations, pricing rationale, follow-up reminders."
              help="Stored for the owner only. Excluded from the client-facing preview, clipboard copy and email summary."
              error={errorsByField.internalCommercialNotes}
              edited={touchedFields.has("internalCommercialNotes")}
              testid="qb-internal-notes"
            />
          </div>
          <TextAreaField
            id="qb-client-notes"
            label="Client-facing notes"
            value={draft.clientFacingNotes}
            onChange={(v) => set("clientFacingNotes", v)}
            placeholder="Anything you want the client to see on the quote itself."
            help="These notes are included verbatim in the client-facing preview and clipboard copy."
            error={errorsByField.clientFacingNotes}
            testid="qb-client-notes"
          />
        </div>
      </SectionShell>

      {/* Sticky action bar — always visible while the operator scrolls. */}
      <div
        className="sticky bottom-0 z-30 -mx-4 border-t border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80"
        data-testid="qb-sticky-actions"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="qb-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {quote?.id ? "Update quote" : "Save draft"}
          </Button>
          {quote?.id && (
            <>
              <Button
                variant="outline"
                onClick={loadPreview}
                className="rounded-sm"
                data-testid="qb-preview"
              >
                {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview client-facing quote"}
              </Button>
              <Button
                variant="outline"
                onClick={approveByOwner}
                className="rounded-sm"
                data-testid="qb-owner-approve"
                disabled={quote.ownerApproved}
              >
                <ShieldCheck className="h-4 w-4" />
                {quote.ownerApproved ? "Owner approved" : "Record owner approval"}
              </Button>
              <Button
                variant="outline"
                onClick={copyInvoicePreparation}
                className="rounded-sm"
                data-testid="qb-copy-invoice-preparation"
                title="Admin-only. Copies structured invoice-ready text to the clipboard. Not a legal invoice."
              >
                <Copy className="h-4 w-4" />
                Copy invoice preparation (admin)
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <label
                  htmlFor="qb-status-select"
                  className="text-xs text-muted-foreground"
                >
                  Status
                </label>
                <select
                  id="qb-status-select"
                  value={currentStatus}
                  onChange={(e) => setStatus(e.target.value)}
                  className="rounded-sm border border-border bg-card px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="qb-status-select"
                >
                  {QUOTE_STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {previewData && (
        <div className="space-y-3" data-testid="quote-client-preview-wrapper">
          <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
            <p className="eyebrow text-emerald-800">Client-facing preview · SBC-001 v1.0</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()} className="rounded-sm" data-testid="qb-print-preview">
                Print / save as PDF
              </Button>
              <Button size="sm" variant="outline" onClick={copyPreview} className="rounded-sm">
                <Copy className="h-4 w-4" />
                Copy as text
              </Button>
            </div>
          </div>
          <ClientDocumentPreview
            data={{
              ...previewData,
              serviceLabel:
                priceItems.find((p) => p.code === draft.selectedServiceCode)?.label ||
                getServiceProfile(draft.selectedServiceCode)?.label ||
                null
            }}
            deliveryCode={deliveryCode}
            internalStatus={quote?.status || "DRAFT"}
            contact={{
              consultantName: "John Kenneally",
              email: "john@spiralbeyondcompliance.com.au",
              website: "spiralbeyondcompliance.com.au"
            }}
            documentGeneratedAt={new Date()}
          />
        </div>
      )}

      <Dialog
        open={Boolean(pendingServiceCode)}
        onOpenChange={(open) => {
          if (!open) cancelServiceChange();
        }}
      >
        <DialogContent data-testid="qb-service-change-dialog" className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">
              Change selected service?
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed text-muted-foreground">
              The selected service will change to{" "}
              <span className="font-mono-label uppercase tracking-[0.14em] text-foreground">
                {dialogTargetService?.label || pendingServiceCode}
              </span>
              . Choose whether to replace the pricing basis, minimum and maximum
              with the new service defaults, or keep the pricing you have already
              entered. All other fields (scope, inclusions, exclusions,
              deliverables, notes, internal commercial notes, provider details,
              quote number, GST setting, status and approval) are preserved
              regardless of your choice.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-sm border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <p className="font-mono-label uppercase tracking-[0.14em] text-foreground">
              New service defaults
            </p>
            <p className="mt-1">
              Basis: {dialogTargetService?.basis || "\u2014"} · Min: $
              {dialogTargetService ? formatAudNumber(String(dialogTargetService.minPrice)) : "\u2014"} ·
              Max: ${dialogTargetService ? formatAudNumber(String(dialogTargetService.maxPrice)) : "\u2014"}
            </p>
          </div>
          <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={cancelServiceChange}
              data-testid="qb-service-change-cancel"
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={confirmKeepPricing}
              data-testid="qb-service-change-keep-pricing"
              className="rounded-sm"
            >
              Change service, keep pricing
            </Button>
            <Button
              type="button"
              onClick={confirmApplyDefaults}
              data-testid="qb-service-change-apply-defaults"
              className="rounded-sm"
            >
              Apply new service defaults
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QB-02B — commercial-content regeneration dialog. Opens after the
          service-change pricing dialog (if a service was changed), or from
          the "Regenerate recommendations" button on the current service. */}
      <Dialog
        open={Boolean(pendingRegen)}
        onOpenChange={(open) => {
          if (!open) skipRegen();
        }}
      >
        <DialogContent data-testid="qb-regen-dialog" className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">
              Regenerate recommended commercial content?
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed text-muted-foreground">
              Loading the{" "}
              <span className="font-mono-label uppercase tracking-[0.14em] text-foreground">
                {regenTargetService?.label || regenTargetProfile?.label || pendingRegen}
              </span>{" "}
              service profile will refresh recommended commercial content in
              scope, inclusions, exclusions, deliverables, evidence and related
              fields. Pricing, GST, provider details, quote identifiers, status
              and approval are never touched by this action.
            </DialogDescription>
          </DialogHeader>
          {regenTouchedFieldsList.length > 0 ? (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-mono-label uppercase tracking-[0.14em]">
                You have consultant-edited content in:
              </p>
              <ul className="mt-1 list-disc pl-5">
                {regenTouchedFieldsList.map((f) => (
                  <li key={f}>{RECOMMENDABLE_FIELD_LABELS[f] || f}</li>
                ))}
              </ul>
              <p className="mt-2">
                By default, edited content is preserved. Choose &quot;Overwrite
                all&quot; only if you want to discard those edits and start
                again from the profile&apos;s recommended content.
              </p>
            </div>
          ) : (
            <div className="rounded-sm border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              No consultant-edited content detected. Applying recommendations will
              refresh the generated fields.
            </div>
          )}
          <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={skipRegen}
              data-testid="qb-regen-cancel"
              className="rounded-sm"
            >
              Cancel
            </Button>
            {regenTouchedFieldsList.length > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={applyRegenOverwriteAll}
                data-testid="qb-regen-overwrite"
                className="rounded-sm"
              >
                Overwrite recommendation fields
              </Button>
            )}
            <Button
              type="button"
              onClick={applyRegenPreservingEdits}
              data-testid="qb-regen-apply"
              className="rounded-sm"
            >
              Apply recommendations, preserve my edits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QB-02C — Recommendation Preview (read-only) */}
      <RecommendationPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        serviceCode={draft.selectedServiceCode}
        onApply={applyFromPreview}
      />
    </div>
  );
}

// -------- Section shell (collapsible) --------

function SectionShell({ name, title, description, open, onToggle, meta, children }) {
  const filled = meta?.filled ?? 0;
  const total = meta?.total ?? 0;
  const hasError = Boolean(meta?.hasError);
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <div
        className={`rounded-sm border ${hasError ? "border-red-300" : "border-border"} bg-card`}
        data-testid={`qb-section-${name}`}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={open}
            data-testid={`qb-section-toggle-${name}`}
          >
            <div className="min-w-0">
              <p className="eyebrow text-foreground">{title}</p>
              {description && (
                <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {hasError ? (
                <span className="inline-flex items-center gap-1 rounded-sm bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">
                  <AlertTriangle className="h-3 w-3" /> needs attention
                </span>
              ) : (
                <span className="rounded-sm bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {filled}/{total} filled
                </span>
              )}
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
              />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border px-4 py-4">{children}</div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// -------- Field primitives --------

function FieldFrame({ id, label, help, error, edited, children }) {
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div>
      <div className="flex items-center gap-2">
        <label
          htmlFor={id}
          className="block text-xs uppercase tracking-[0.16em] text-muted-foreground"
        >
          {label}
        </label>
        {edited && (
          <span
            className="inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-medium uppercase tracking-[0.1em] text-amber-800 print:hidden"
            data-testid={`${id}-edited-indicator`}
            aria-label="Consultant edited"
          >
            <span aria-hidden="true">•</span> Edited
          </span>
        )}
      </div>
      {typeof children === "function" ? children(describedBy) : children}
      {help && (
        <p id={helpId} className="mt-1 text-xs text-muted-foreground">
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function TextField({ id, label, value, onChange, placeholder, help, error, edited, type = "text", testid }) {
  return (
    <FieldFrame id={id} label={label} help={help} error={error} edited={edited}>
      {(describedBy) => (
        <Input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`mt-1 rounded-sm ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
          aria-describedby={describedBy}
          aria-invalid={Boolean(error) || undefined}
          data-testid={testid}
        />
      )}
    </FieldFrame>
  );
}

function TextAreaField({ id, label, value, onChange, placeholder, help, error, edited, testid }) {
  return (
    <FieldFrame id={id} label={label} help={help} error={error} edited={edited}>
      {(describedBy) => (
        <Textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className={`mt-1 rounded-sm ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
          aria-describedby={describedBy}
          aria-invalid={Boolean(error) || undefined}
          data-testid={testid}
        />
      )}
    </FieldFrame>
  );
}

function NumericField({ id, label, value, onChange, placeholder, help, error, testid }) {
  return (
    <FieldFrame id={id} label={label} help={help} error={error}>
      {(describedBy) => (
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`mt-1 rounded-sm ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
          aria-describedby={describedBy}
          aria-invalid={Boolean(error) || undefined}
          data-testid={testid}
        />
      )}
    </FieldFrame>
  );
}

// Presentation-only currency input. Stores a raw numeric string in state
// (never a formatted "$1,500" string). Displays formatted when blurred and
// raw digits while focused, so editing, deletion and pasting remain reliable.
function CurrencyField({ id, label, value, onChange, placeholder, help, error, testid }) {
  const [focused, setFocused] = useState(false);
  const displayValue = focused ? String(value ?? "") : formatAudNumber(value);
  const inputRef = useRef(null);
  return (
    <FieldFrame id={id} label={label} help={help} error={error}>
      {(describedBy) => (
        <div className="relative mt-1">
          <span
            className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground"
            aria-hidden="true"
          >
            $
          </span>
          <Input
            id={id}
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={displayValue}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(e) => onChange(sanitiseCurrencyInput(e.target.value))}
            onPaste={(e) => {
              const pasted = (e.clipboardData || window.clipboardData).getData("text");
              if (pasted && /[^0-9.,]/.test(pasted)) {
                e.preventDefault();
                onChange(sanitiseCurrencyInput(pasted));
              }
              // Otherwise let the default paste happen; sanitisation runs on
              // the resulting onChange.
            }}
            placeholder={placeholder}
            className={`rounded-sm pl-6 ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
            aria-describedby={describedBy}
            aria-invalid={Boolean(error) || undefined}
            data-testid={testid}
          />
        </div>
      )}
    </FieldFrame>
  );
}

function SelectField({ id, label, value, onChange, options, help, error }) {
  return (
    <FieldFrame id={id} label={label} help={help} error={error}>
      {(describedBy) => (
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`mt-1 w-full rounded-sm border ${error ? "border-red-400" : "border-border"} bg-card px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
          aria-describedby={describedBy}
          aria-invalid={Boolean(error) || undefined}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
    </FieldFrame>
  );
}

// Dropdown of canonical options with an "Other (specify)" text-fallback.
// Selecting a canonical option updates the value directly. Choosing "Other"
// switches to a free-text input; the current value is preserved so legacy
// data is never lost.
function SelectWithOtherField({
  id,
  label,
  value,
  onChange,
  options,
  customMode,
  setCustomMode,
  help,
  error
}) {
  const OTHER = "__other__";
  const selectValue = customMode ? OTHER : value || "";
  return (
    <FieldFrame id={id} label={label} help={help} error={error}>
      {(describedBy) => (
        <>
          <select
            id={id}
            value={selectValue}
            onChange={(e) => {
              const v = e.target.value;
              if (v === OTHER) {
                setCustomMode(true);
                // Keep any previous value so the operator can edit it below.
                return;
              }
              setCustomMode(false);
              onChange(v);
            }}
            className={`mt-1 w-full rounded-sm border ${error ? "border-red-400" : "border-border"} bg-card px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
            aria-describedby={describedBy}
            aria-invalid={Boolean(error) || undefined}
          >
            <option value="">— select —</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
            <option value={OTHER}>Other (specify)</option>
          </select>
          {customMode && (
            <Input
              type="text"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Type a short custom value"
              className={`mt-2 rounded-sm ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
              aria-label={`${label} custom value`}
            />
          )}
        </>
      )}
    </FieldFrame>
  );
}

// Calendar-picker date field. Stores ISO YYYY-MM-DD. Displays DD/MM/YYYY.
function DateField({ id, label, value, onChange, help, error, testid }) {
  const [open, setOpen] = useState(false);
  const parsed = parseIsoDate(value);
  const displayText = value ? formatDateDisplay(value) : "";
  return (
    <FieldFrame id={id} label={label} help={help} error={error}>
      {(describedBy) => (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              id={id}
              type="button"
              className={`mt-1 inline-flex w-full items-center justify-between rounded-sm border bg-card px-3 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${error ? "border-red-400" : "border-border"}`}
              aria-describedby={describedBy}
              aria-invalid={Boolean(error) || undefined}
              data-testid={testid}
            >
              <span className={displayText ? "text-foreground" : "text-muted-foreground"}>
                {displayText || "DD/MM/YYYY"}
              </span>
              <CalendarIcon className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={parsed || undefined}
              onSelect={(d) => {
                onChange(toIsoDate(d));
                setOpen(false);
              }}
              initialFocus
            />
            <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
              <button
                type="button"
                className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                data-testid={testid ? `${testid}-clear` : undefined}
              >
                Clear
              </button>
              <span className="text-muted-foreground">Displayed as DD/MM/YYYY</span>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </FieldFrame>
  );
}
